import { useContext, useEffect, useState } from 'react';
import AuthAxios from '../../axios';
import { FirebaseAnalyticsEvent, Login } from '../../types';
import { useNavigate } from 'react-router-dom';
import { AuthStateContext } from '../../contexts/AuthContext';
import { ModalContext } from '../../contexts/ModalContext';
import { getItem, LocalLanguage, setItem, USERINFO, USERTOKEN, RECENT_SOCIAL_LOGIN } from '../../utils/localStorage';
import FacebookLoginBtn from './FacebookLoginBtn';
import AppleLoginBtn from './AppleLoginBtn';
import GoogleLoginBtn from './GoogleLoginBtn';
import NaverLoginBtn from './NaverLoginBtn.jsx';
import KakaoLoginBtn from './KakaoLoginBtn';
import { logFirebaseEvent } from '../../utils/firebase/analytics';
import { SocialLoginPlatformEnum } from './type';
import { t } from 'i18next';
import { consumePostAuthLandingRedirect } from '../../utils/postAuthLandingRedirect';
import { SocialDuplicateEmailModal } from './SocialDuplicateEmailModal';

export default function SocialLoginButtons({ variant = 'legacy' }: { variant?: 'legacy' | 'draft' }) {
  const lan = getItem(LocalLanguage);
  const navigate = useNavigate();
  const { setLoginSuccess } = useContext(AuthStateContext);
  const { handleModal } = useContext(ModalContext);
  const [ recentLogin, setRecentLogin ] = useState<SocialLoginPlatformEnum | undefined>(undefined);
  const [ tooltipStyle, setTooltipStyle ] = useState('0px');

  const postSignIn = async ({
    type,
    socialId,
    email,
    forceSignUp,
  }: {
    type: string;
    socialId: string;
    email: string;
    forceSignUp?: boolean;
  }) => {
    return AuthAxios.post<Login>('/auth/social', {
      type,
      socialId,
      email,
      forceSignUp,
      shouldGenerateNickname: true, // web 은 닉네임 자동 생성하고, 성별, 생년월일 입력 페이지 없음.
    })
      .then((resolve) => {
        const { user, accessToken, refreshToken } = resolve.data;

        //subscriptionInfo 나중에 로그인 후 구독권 만료에 대한 고지용 팝업 .
        setItem(USERINFO, user);
        setItem(USERTOKEN, { accessToken, refreshToken });
        setLoginSuccess(user);

        return user;
      })
      .then(async (user) => {
        if (user.termsAgreed) {
          logFirebaseEvent(FirebaseAnalyticsEvent.LOGIN, {
            user_uuid: user.id,
            login_platform: user.type,
          });
          const redirectPath = consumePostAuthLandingRedirect();
          const afterLoginRedirectPage = redirectPath ?? '/app/home';
          return navigate(afterLoginRedirectPage);
        }
        return navigate('/auth/agrement');
      })
      .catch((error) => {
        navigate('/auth', { replace: true });
        if (
          error.response.status === 409 &&
          (error.response.data.message as string).indexOf('email already exists') !== -1
        ) {
          const { email, socials } = error.response.data.options;
          handleModal(
            <SocialDuplicateEmailModal
              email={email}
              social={socials}
              onCancel={() => handleModal()}
              onContinue={() => {
                handleModal();
                postSignIn({ type, socialId, email, forceSignUp: true });
              }}
            />,
          );
          return;
        }

        let message = error.response.data.message;
        if (error.response.data.message === '재가입은 탈퇴 3개월 이후부터 가능합니다.' && lan === 'en') {
          message = 'You can re-sign up 3 months after deleting your account.';
        }
        handleModal(
          <div className="w-[300] bg-N10 flex flex-col items-center rounded-[22px] text-N0 p-2 px-4">
            <div>{message ?? ''}</div>
            <button
              className="w-full bg-N5 text-N10 rounded-[22px] text-base font-semibold"
              onClick={() => handleModal()}
            >
              {lan === 'en' ? 'ok' : '확인'}
            </button>
          </div>
        );
      });
  };

  const afterSocialLoginSuccess = ({ type, socialId, email }: { type: SocialLoginPlatformEnum; socialId: string; email: string }) => {
    setRecentLogin(type);
    setItem(RECENT_SOCIAL_LOGIN, type);
    return postSignIn({ type, socialId, email });
  };

  // 컴포넌트 Mount시 처음 실행
  useEffect(() => {
    // 컴포넌트 마운트 시 최근 로그인 정보 불러오기
    const recentSocial = getItem(RECENT_SOCIAL_LOGIN);
    setRecentLogin(recentSocial);
  }, []);

  useEffect(() => {
    if (variant === 'draft' || !recentLogin) return;

    const targetElement = document.querySelector(`[data-social="${recentLogin}"]`);
    if (targetElement) {
      const parentRect = (targetElement.closest('.social-buttons-container') as HTMLElement).getBoundingClientRect();
      const targetRect = targetElement.getBoundingClientRect();
      const left = targetRect.left - parentRect.left + targetRect.width / 2 - 10;
      setTooltipStyle(`${left}px`);
    }
  }, [recentLogin, variant]);

  const sharedProps = { variant, recentLogin };

  return (
    <div className={`flex w-full flex-col gap-4 text-center md:gap-3 ${variant === 'draft' ? '' : ''}`}>
      {variant === 'legacy' && <p className="block text-white md:hidden">{t('login.start')}</p>}
      {variant === 'draft' && recentLogin && (
        <p className="login-recent-badge">{t('login.latest')}</p>
      )}
      <div
        className={`social-buttons-container relative ${
          variant === 'draft' ? '' : 'flex flex-row gap-[10px] md:flex-col'
        }`}
      >
        <KakaoLoginBtn
          afterSocialLoginSuccess={afterSocialLoginSuccess}
          dataSocial={SocialLoginPlatformEnum.KAKAO}
          {...sharedProps}
        />
        <GoogleLoginBtn
          afterSocialLoginSuccess={afterSocialLoginSuccess}
          dataSocial={SocialLoginPlatformEnum.GOOGLE}
          {...sharedProps}
        />
        <NaverLoginBtn
          afterSocialLoginSuccess={afterSocialLoginSuccess}
          dataSocial={SocialLoginPlatformEnum.NAVER}
          {...sharedProps}
        />
        <AppleLoginBtn
          afterSocialLoginSuccess={afterSocialLoginSuccess}
          dataSocial={SocialLoginPlatformEnum.APPLE}
          {...sharedProps}
        />
      </div>
      {variant === 'legacy' && (
        <div className={`${recentLogin ? 'block' : 'hidden'} relative pt-[15px] md:hidden`}>
          <div className="h-[36px] w-full rounded-full bg-gray-700 px-4 py-2 text-sm text-white">
            {t('login.latest')}
            <div
              className="absolute top-0 h-[0] w-[0] border-b-[22px] border-l-[10px] border-r-[10px] border-b-gray-700 border-l-transparent border-r-transparent"
              style={{ left: tooltipStyle }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
